/**
 * MeasureToolBase - Thin facade over MeasureEngine for BaseTool subclasses.
 *
 * Delegates all measurement logic to MeasureEngine while exposing the same
 * protected API that existing subclasses (Point, Distance, Area) depend on.
 * This allows those tools to work without any changes.
 *
 * Tools that can't extend MeasureToolBase (e.g. ManualRoomDrawTool extends
 * FloorplanToolBase) can compose MeasureEngine directly.
 */

import * as THREE from 'three';
import { BaseTool } from '../../BaseTool';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { MeasureTools } from '../../../utils/Measure';
import { PointoramaAnnotation } from '../../../utils/PointoramaAnnotation';
import { SphereMarker } from '../../../utils/SphereMarker';
import { ProjectionPlaneSnapping, MeasureAngleSnapping } from '../modules/shared';
import { MeasureEngine, LineIntersect, isBasicMeasurement } from '../modules/MeasureEngine';

export abstract class MeasureToolBase extends BaseTool {
  /** Each tool defines its measure type */
  abstract readonly measureType: MeasureTools;

  /** Each tool creates its own annotation type */
  protected abstract createAnnotation(): PointoramaAnnotation;

  /** The underlying measurement engine */
  private _engine!: MeasureEngine;
  private previousDoubleClickZoomState = true;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.initUndoRedo();
  }

  // =========================================================================
  // Proxy getters — expose engine state to subclasses
  // =========================================================================

  protected get currentMeasure(): PointoramaAnnotation {
    return this._engine.currentMeasure;
  }
  protected set currentMeasure(value: PointoramaAnnotation) {
    this._engine.currentMeasure = value;
  }

  protected get hoveredSphere(): SphereMarker | undefined {
    return this._engine.hoveredSphere;
  }
  protected get hoveredLineIntersect(): LineIntersect | undefined {
    return this._engine.hoveredLineIntersect;
  }
  protected get draggedSphere(): SphereMarker | undefined {
    return this._engine.draggedSphere;
  }
  protected get lastMovedMeasure(): PointoramaAnnotation | null {
    return this._engine.lastMovedMeasure;
  }

  protected get planeSnapping(): ProjectionPlaneSnapping {
    return this._engine.planeSnapping;
  }
  protected get angleSnappingModule(): MeasureAngleSnapping {
    return this._engine.angleSnappingModule;
  }

  protected get measureScene(): THREE.Scene {
    return this._engine.measureScene;
  }

  // =========================================================================
  // Proxy methods — delegate to engine
  // =========================================================================

  // Hover
  protected updateHoverState(): void {
    this._engine.updateHoverState();
  }
  protected setHoveredSphere(): void {
    this._engine.setHoveredSphere();
  }
  protected setHoveredLineIntersect(): void {
    this._engine.setHoveredLineIntersect();
  }

  // Drag
  protected isDraggingSphere(): boolean {
    return this._engine.isDraggingSphere();
  }
  protected onFirstDragDetected(): void {
    this._engine.onFirstDragDetected();
  }
  protected startDraggingEdge(lineIntersect: LineIntersect): void {
    this._engine.startDraggingEdge(lineIntersect);
  }
  protected onDragPoint(location: THREE.Vector3): void {
    this._engine.onDragPoint(location);
  }
  protected onDragPointEnd(): void {
    this._engine.onDragPointEnd();
  }

  // Point resolution
  protected getPointUnderMouse(mouse: THREE.Vector2, angleSnapping = false): THREE.Vector3 | null {
    return this._engine.getPointUnderMouse(mouse, angleSnapping);
  }

  // Drawing
  protected drawNewMeasure(mouse: THREE.Vector2, angleSnapping = false): void {
    this._engine.drawNewMeasure(mouse, angleSnapping);
  }
  protected updateCurrentMeasureVisibility(): void {
    this._engine.updateCurrentMeasureVisibility();
  }
  protected updateVisuals(): void {
    this._engine.updateVisuals();
  }

  // Measure lifecycle
  protected startMeasure(): void {
    this._engine.startMeasure();
  }
  protected drawingInProgress(): boolean {
    return this._engine.drawingInProgress();
  }
  protected canPlacePoint(): boolean {
    return this._engine.canPlacePoint();
  }
  protected placePoint(): void {
    this._engine.placePoint();
  }
  protected hoveringFirstPoint(): boolean {
    return this._engine.hoveringFirstPoint();
  }

  // Editing
  protected undoCurrentMeasure(): boolean {
    return this._engine.undoCurrentMeasure();
  }
  protected removeMarker(measure: PointoramaAnnotation, pointIndex: number): void {
    this._engine.removeMarker(measure, pointIndex);
  }
  protected setMarkerSizes(): void {
    this._engine.setMarkerSizes();
  }

  // Utility
  protected getMouseFromEvent(event: MouseEvent): THREE.Vector2 {
    return this._engine.getMouseFromEvent(event);
  }
  protected snapToPerpendicular(lineIntersect: LineIntersect): THREE.Vector3 {
    return this._engine.snapToPerpendicular(lineIntersect);
  }

  // =========================================================================
  // Finish — calls engine + dispatches to scene (subclasses can override)
  // =========================================================================

  /** Minimum sphere count (placed points + preview) to allow finishing. Override per tool. */
  protected readonly minSpheresToFinish: number = 2;

  /** Finish the current multi-point measurement. Removes trailing preview marker, dispatches to scene, restarts. */
  protected finishCurrentMeasurement(): void {
    const completed = this._engine.completeCurrentMeasurement();
    if (completed) {
      this.viewer.scene.finishMeasurement(completed);
    }
  }

  // =========================================================================
  // Lifecycle — creates/disposes engine
  // =========================================================================

  protected onEnable(): void {
    this._engine = new MeasureEngine(this.viewer, this.measureType, {
      createAnnotation: () => this.createAnnotation(),
      minSpheresToFinish: this.minSpheresToFinish,
      undoManager: this.undoRedo ?? undefined,
      isUserDragging: () => this.isDragging,
      // The basic measure tools (point/line/area) may edit each other's
      // annotations, but not kind-backed ones (rooms, openings, …).
      canEditAnnotation: isBasicMeasurement,
    });
    this._engine.init();

    // Disable double-click zoom while drawing
    if (this.viewer.orbitControls) {
      this.previousDoubleClickZoomState = this.viewer.orbitControls.doubleClickZoomEnabled;
      this.viewer.orbitControls.doubleClickZoomEnabled = false;
    }

    // Call subclass hook
    this.onToolEnable();
  }

  protected onDisable(): void {
    // Call subclass hook first
    this.onToolDisable();

    this._engine.dispose();
    this.viewer.inputHandler.unblockOrbitControls();

    // Restore double-click zoom
    if (this.viewer.orbitControls) {
      this.viewer.orbitControls.doubleClickZoomEnabled = this.previousDoubleClickZoomState;
    }
  }

  /** Optional hook for subclasses on enable */
  protected onToolEnable(): void {
    // noop
  }

  /** Optional hook for subclasses on disable */
  protected onToolDisable(): void {
    // noop
  }

  // =========================================================================
  // Orbit control blocking (needs DOM event, stays in tool)
  // =========================================================================

  /**
   * Block orbit controls when mousedown occurs on a draggable sphere/edge.
   * Runs before any mousemove, so orbit is blocked from the very first frame.
   * Subclasses that override onMouseDown should call super.onMouseDown(event).
   */
  public onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;

    if (this._engine.shouldBlockOrbitControls()) {
      this.viewer.inputHandler.blockOrbitControls();
    }
  }

  /** Unblock orbit controls on mouse release. */
  public onMouseUp(_event: MouseEvent): void {
    this.viewer.inputHandler.unblockOrbitControls();
  }
}
